import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import moment from "moment";
import {
  HOUR_HEIGHT, TIME_COLUMN_WIDTH, TIMELINE_HEIGHT,
  CALENDAR_ACCENT, GRID_LINE_COLOR,
} from "./CalendarConstants";
import { isAllDay, durationMinutes, dateKey } from "./CalendarUtils";
import { layoutEvents } from "./EventLayoutHelper";
import EventBlock from "./EventBlock";
import CurrentTimeIndicator from "./CurrentTimeIndicator";
import AllDayEventsBar from "./AllDayEventsBar";
import OffscreenEventsIndicator from "./OffscreenEventsIndicator";

const WeekView = ({ focusedDay, events, onEventTap, onEmptySlotTap, onDayHeaderTap }) => {
  const scrollRef = useRef(null);
  const [eventsAbove, setEventsAbove] = useState(0);
  const [eventsBelow, setEventsBelow] = useState(0);

  const weekStart = useMemo(() => moment(focusedDay).startOf("isoWeek"), [focusedDay]);
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, "days")),
    [weekStart]
  );

  // Group events by date
  const eventsByDay = useMemo(() => {
    const map = {};
    for (const day of weekDays) {
      const key = day.format("YYYY-MM-DD");
      map[key] = { allDay: [], timed: [] };
    }
    for (const event of events) {
      const key = dateKey(event.start);
      if (!map[key]) continue;
      if (isAllDay(event)) map[key].allDay.push(event);
      else map[key].timed.push(event);
    }
    return map;
  }, [events, weekDays]);

  const allDayEvents = useMemo(() => {
    const result = [];
    for (const day of weekDays) {
      const key = day.format("YYYY-MM-DD");
      if (eventsByDay[key]) result.push(...eventsByDay[key].allDay);
    }
    return result;
  }, [eventsByDay, weekDays]);

  // Offscreen counting
  const updateOffscreenCounts = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const visibleTop = el.scrollTop;
    const visibleBottom = el.scrollTop + el.clientHeight;
    let above = 0, below = 0;

    for (const day of weekDays) {
      const key = day.format("YYYY-MM-DD");
      const timed = eventsByDay[key]?.timed || [];
      for (const event of timed) {
        const s = moment(event.start);
        const eventTop = (s.hours() + s.minutes() / 60) * HOUR_HEIGHT;
        const eventBottom = eventTop + (durationMinutes(event) / 60) * HOUR_HEIGHT;
        if (eventBottom <= visibleTop) above++;
        else if (eventTop >= visibleBottom) below++;
      }
    }
    setEventsAbove(above);
    setEventsBelow(below);
  }, [weekDays, eventsByDay]);

  // Auto-scroll on mount
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => {
      const now = new Date();
      const target = Math.max(0, (now.getHours() - 1) * HOUR_HEIGHT);
      el.scrollTo({ top: Math.min(target, el.scrollHeight - el.clientHeight) });
      updateOffscreenCounts();
    });
    return () => cancelAnimationFrame(raf);
  }, [focusedDay, updateOffscreenCounts]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateOffscreenCounts, { passive: true });
    return () => el.removeEventListener("scroll", updateOffscreenCounts);
  }, [updateOffscreenCounts]);

  const scrollToEvents = (above) => {
    const el = scrollRef.current;
    if (!el) return;
    const allTimed = [];
    for (const day of weekDays) {
      const key = day.format("YYYY-MM-DD");
      allTimed.push(...(eventsByDay[key]?.timed || []));
    }
    if (!allTimed.length) return;

    if (above) {
      let earliest = Infinity;
      for (const event of allTimed) {
        const s = moment(event.start);
        const t = (s.hours() + s.minutes() / 60) * HOUR_HEIGHT;
        if (t < earliest) earliest = t;
      }
      el.scrollTo({ top: Math.max(0, earliest - 20), behavior: "smooth" });
    } else {
      let latest = 0;
      for (const event of allTimed) {
        const s = moment(event.start);
        const t = (s.hours() + s.minutes() / 60) * HOUR_HEIGHT;
        if (t > latest) latest = t;
      }
      el.scrollTo({ top: Math.max(0, latest - el.clientHeight + 80), behavior: "smooth" });
    }
  };

  const today = moment();

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      {/* Day headers */}
      <div style={{
        display: "flex",
        backgroundColor: "#fff",
        borderBottom: "1px solid #E0E0E0",
      }}>
        <div style={{ width: TIME_COLUMN_WIDTH, flexShrink: 0 }} />
        {weekDays.map((day) => {
          const isToday = day.isSame(today, "day");
          return (
            <div
              key={day.format("YYYY-MM-DD")}
              onClick={() => onDayHeaderTap(day.toDate())}
              style={{
                flex: 1,
                textAlign: "center",
                padding: "8px 0",
                cursor: "pointer",
              }}
            >
              <div style={{
                fontSize: 10,
                fontWeight: 600,
                color: isToday ? CALENDAR_ACCENT : "#9E9E9E",
                textTransform: "uppercase",
              }}>
                {day.format("dd")}
              </div>
              <div style={{
                width: 28,
                height: 28,
                lineHeight: "28px",
                margin: "2px auto 0",
                borderRadius: "50%",
                fontSize: 14,
                fontWeight: 600,
                color: isToday ? "#fff" : "rgba(0,0,0,0.87)",
                backgroundColor: isToday ? CALENDAR_ACCENT : "transparent",
              }}>
                {day.date()}
              </div>
            </div>
          );
        })}
      </div>

      <AllDayEventsBar events={allDayEvents} onTap={onEventTap} />

      {/* Timeline */}
      <div style={{ position: "relative", flex: 1, overflow: "hidden" }}>
        <div
          ref={scrollRef}
          style={{
            height: "100%",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div style={{ display: "flex", height: TIMELINE_HEIGHT }}>
            {/* Time labels column */}
            <div style={{ width: TIME_COLUMN_WIDTH, flexShrink: 0, position: "relative", height: TIMELINE_HEIGHT }}>
              {Array.from({ length: 24 }, (_, hour) => (
                <div key={hour} style={{
                  position: "absolute",
                  top: hour * HOUR_HEIGHT - 7,
                  right: 8,
                  fontSize: 11,
                  fontWeight: 500,
                  color: "#9E9E9E",
                  userSelect: "none",
                }}>
                  {String(hour).padStart(2, "0")}:00
                </div>
              ))}
            </div>

            {/* Day columns */}
            {weekDays.map((day) => {
              const key = day.format("YYYY-MM-DD");
              const timedEvents = eventsByDay[key]?.timed || [];
              const entries = layoutEvents(timedEvents);
              const isToday = day.isSame(today, "day");

              return (
                <DayColumn
                  key={key}
                  day={day}
                  entries={entries}
                  isToday={isToday}
                  onEventTap={onEventTap}
                  onEmptySlotTap={onEmptySlotTap}
                  scrollRef={scrollRef}
                />
              );
            })}
          </div>
        </div>
        <OffscreenEventsIndicator count={eventsAbove} isAbove onTap={() => scrollToEvents(true)} />
        <OffscreenEventsIndicator count={eventsBelow} isAbove={false} onTap={() => scrollToEvents(false)} />
      </div>
    </div>
  );
};

/** Single day column in the week timeline */
const DayColumn = React.memo(({ day, entries, isToday, onEventTap, onEmptySlotTap, scrollRef }) => {
  const colRef = useRef(null);

  const handleClick = (e) => {
    const rect = colRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top + (scrollRef.current?.scrollTop || 0);

    const hour = Math.floor(y / HOUR_HEIGHT);
    const minuteFrac = (y % HOUR_HEIGHT) / HOUR_HEIGHT;
    const roundedMinute = Math.floor((minuteFrac * 60) / 15) * 15;

    const tapped = moment(day).startOf("day").add(hour, "hours").add(roundedMinute, "minutes");
    onEmptySlotTap(tapped.toDate());
  };

  return (
    <div
      ref={colRef}
      onClick={handleClick}
      style={{
        flex: 1,
        position: "relative",
        height: TIMELINE_HEIGHT,
        borderLeft: `0.5px solid ${GRID_LINE_COLOR}`,
      }}
    >
      {/* Hour grid lines */}
      {Array.from({ length: 25 }, (_, hour) => (
        <div key={hour} style={{
          position: "absolute",
          top: hour * HOUR_HEIGHT,
          left: 0,
          right: 0,
          height: 0.5,
          backgroundColor: GRID_LINE_COLOR,
        }} />
      ))}

      {/* Event blocks */}
      {entries.map((entry) => {
        const { event, column, totalColumns } = entry;
        const s = moment(event.start);
        const top = (s.hours() + s.minutes() / 60) * HOUR_HEIGHT;
        const height = (durationMinutes(event) / 60) * HOUR_HEIGHT;
        const colWidth = colRef.current
          ? colRef.current.clientWidth / Math.max(totalColumns, 1)
          : 100 / Math.max(totalColumns, 1);

        return (
          <EventBlock
            key={event.id || `${event.title}-${s.valueOf()}`}
            event={event}
            top={top}
            height={height}
            left={column * colWidth}
            width={colWidth}
            onTap={onEventTap}
          />
        );
      })}

      {/* Current time indicator */}
      {isToday && <CurrentTimeIndicator variant="week" />}
    </div>
  );
});

export default WeekView;
